//Dependencies............................................
import React, { useState } from "react";
import axios from "axios";
import { Button, Card, Table, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

//Components..............................................
import { BASE_URL } from "../../../utils/constants";
import { JsonSchemaItem, JsonSchemaListResponse } from "../../../utils/interfaces";

import JsonSchemaListViewAddModal from "../../../Components/FirmwareManagerPageComp/Modals/AddJsonSchemaListModal";
import JsonSchemaListViewEditModal from "../../../Components/FirmwareManagerPageComp/Modals/EditJsonSchemaListModal";
import DeleteModal from "../../GlobalComponents/Modals/DeleteModal";
import { NotificationInstance } from "antd/es/notification/interface";
import { useHasAction } from "../../../hooks/useHasAction";

interface Props {
    api: NotificationInstance
}

const JsonSchemaListView: React.FC<Props> = ({ api }) => {
  const canAddJsonSchema: boolean = useHasAction("firmware_manager_add_json_schema");
  const canEditJsonSchema: boolean = useHasAction("firmware_manager_edit_json_schema");
  const canDeleteJsonSchema: boolean = useHasAction("firmware_manager_delete_json_schema");

  const queryClient = useQueryClient();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJsonSchema, setSelectedJsonSchema] =
    useState<JsonSchemaItem>();
  const [jsonSchemaEdit, setJsonSchemaEdit] = useState<JsonSchemaItem>();

  const jsonSchemaListQuery = useQuery({
    queryKey: ["jsonSchemaList"],
    queryFn: async () => {
      const res = await axios.get<JsonSchemaListResponse>(
        `${BASE_URL}/get_json_schemas_base`
      );
      return res.data.content;
    },
  });

  const deleteJsonSchema = useMutation({
    mutationFn: (id: number) =>
      axios.post(`${BASE_URL}/delete_json_schema_base/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jsonSchemaList"] });
      queryClient.invalidateQueries({ queryKey: ["baseFirmwareList"] });
      queryClient.invalidateQueries({ queryKey: ["vehicleList"] });
      api.success({
        title: "Schema Deleted",
        description: `The JSON Schema was deleted successfully.`,
        placement: "bottomRight",
        showProgress: true
      });
    },
    onError: (error: any) => {
      api.error({
        title: "Error Deleting Schema",
        description: `Failed to delete JSON Schema: ${error.message}`,
        placement: "bottomRight",
        showProgress: true
      });
    },
  });

  const handleDelete = (jsonSchema: JsonSchemaItem) => {
    setSelectedJsonSchema(jsonSchema);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    deleteJsonSchema.mutate(selectedJsonSchema!.id);
    setShowDeleteModal(false);
  };
  const toggleEditModal = () => {
    setShowEditModal(!showEditModal);
  };

  const toggleAddModal = () => {
    setShowAddModal(!showAddModal);
  };

  // Columns definition for Ant Design Table
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Created at",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "Schema Version Name",
      dataIndex: "version_schema_name",
      key: "version_schema_name",
    },
    {
      title: "Json Config",
      dataIndex: "json_schema",
      key: "json_schema",
      render: (_text: any, record: any) => (
        <Tooltip title="Edit JSON Schema">
          <Button
            icon={<EditOutlined/>}
            disabled={!canEditJsonSchema}
            onClick={() => handleJsonSchemaEdit(record)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_text: any, record: any) => (
        <div>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={!canDeleteJsonSchema}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const handleJsonSchemaEdit = (record: JsonSchemaItem) => {
    setJsonSchemaEdit(record);
    console.log(record);
    toggleEditModal();
  };

  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            JSON Schema Management
            <Tooltip title="Add New Schema">
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                onClick={toggleAddModal}
                disabled={!canAddJsonSchema}
              />
            </Tooltip>
          </div>
        }
      >
        <JsonSchemaListViewEditModal
          jsonSchema={jsonSchemaEdit!}
          showModal={showEditModal}
          onClose={toggleEditModal}
          api={api}
        />
        <JsonSchemaListViewAddModal
          showModal={showAddModal}
          onClose={toggleAddModal}
          api={api}
        />
        <DeleteModal
          showModal={showDeleteModal}
          onDelete={() => confirmDelete()}
          onClose={() => setShowDeleteModal(false)}
        />
        <Table
          columns={columns}
          dataSource={jsonSchemaListQuery.data}
          loading={jsonSchemaListQuery.isLoading}
          rowKey="id"
        />
      </Card>
    </div>
  );
};

export default JsonSchemaListView;
